import React from "react";
import { Search, ChevronUp, ChevronDown, Check, Instagram } from "lucide-react";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";

export interface InfluencerSearchItem {
  id: string;
  name?: string;
  handle?: string;
  platform?: string;
  avatar?: string;
  isVerified?: boolean;
}

interface InfluencerSearchSelectProps {
  influencers: InfluencerSearchItem[];
  selectedInfluencerId: string;
  onSelectInfluencer: (id: string) => void;
  isLoading: boolean;
  isError: boolean;
  search: string;
  onSearchChange: (value: string) => void;
  isDropdownOpen: boolean;
  onToggleDropdown: () => void;
  onOpenDropdown: () => void;
  emptyMessage: string;
}

export const InfluencerSearchSelect: React.FC<InfluencerSearchSelectProps> = ({
  influencers,
  selectedInfluencerId,
  onSelectInfluencer,
  isLoading,
  isError,
  search,
  onSearchChange,
  isDropdownOpen,
  onToggleDropdown,
  onOpenDropdown,
  emptyMessage,
}) => {
  const selectedInfluencerObj = influencers.find(
    (inf) => inf?.id === selectedInfluencerId
  );

  const filteredInfluencers = influencers.filter((inf) => {
    if (!inf) return false;
    const q = (search || "").toLowerCase().trim();
    if (!q) return true;
    const nameMatch =
      typeof inf.name === "string" && inf.name.toLowerCase().includes(q);
    const handleMatch =
      typeof inf.handle === "string" && inf.handle.toLowerCase().includes(q);
    return nameMatch || handleMatch;
  });

  return (
    <div className="space-y-2">
      <Label
        htmlFor="influencer-search"
        className="text-[14px] font-bold text-slate-800"
      >
        Select Influencer
      </Label>

      <div className="rounded-[16px] border border-[#A655F8] bg-white shadow-sm overflow-hidden transition-all">
        {/* Single Combined Search & Trigger Bar */}
        <div className="flex items-center justify-between p-3 px-4">
          <div className="flex items-center gap-3 min-w-0 flex-1">
            <Search className="w-5 h-5 text-slate-400 shrink-0" />
            <input
              id="influencer-search"
              type="text"
              value={search}
              onChange={(e) => {
                onSearchChange(e.target.value);
                if (!isDropdownOpen) onOpenDropdown();
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                }
              }}
              onFocus={onOpenDropdown}
              placeholder={
                selectedInfluencerObj
                  ? `${selectedInfluencerObj.name} (@${selectedInfluencerObj.handle})`
                  : "Search and select an influencer..."
              }
              className="w-full bg-transparent text-slate-800 placeholder-slate-400 text-[14.5px] focus:outline-none"
            />
            {selectedInfluencerObj && !search && (
              <div className="hidden sm:flex items-center gap-1.5 bg-[#FAF5FF] text-[#A655F8] px-2.5 py-0.5 rounded-full text-xs font-semibold shrink-0 border border-[#A655F8]/20">
                <span>Selected</span>
              </div>
            )}
          </div>
          <button
            type="button"
            onClick={onToggleDropdown}
            className="text-slate-400 hover:text-slate-600 focus:outline-none ml-2 p-1"
            aria-label="Toggle dropdown"
          >
            {isDropdownOpen ? (
              <ChevronUp className="w-5 h-5" />
            ) : (
              <ChevronDown className="w-5 h-5" />
            )}
          </button>
        </div>

        {isDropdownOpen && (
          <div className="border-t border-slate-100/80">
            {isLoading ? (
              <div className="py-6 text-center text-sm text-slate-500">
                Loading influencers...
              </div>
            ) : isError ? (
              <div className="py-6 text-center text-sm text-red-500">
                {emptyMessage}
              </div>
            ) : filteredInfluencers.length === 0 ? (
              <div className="py-6 text-center text-sm text-slate-500">
                {influencers.length === 0
                  ? emptyMessage
                  : "No influencers found matching your search."}
              </div>
            ) : (
              <div className="max-h-[250px] overflow-y-auto divide-y divide-slate-100/80 overscroll-contain">
                {filteredInfluencers.map((influencer) => {
                  if (!influencer) return null;
                  const isSelected = selectedInfluencerId === influencer.id;
                  const name =
                    influencer.name || influencer.handle || "Influencer";
                  const initial = (name.charAt(0) || "I").toUpperCase();
                  const handle = influencer.handle || "";
                  const platform = influencer.platform || "Instagram";

                  return (
                    <button
                      key={influencer.id}
                      type="button"
                      onClick={() => {
                        onSelectInfluencer(influencer.id);
                      }}
                      className={cn(
                        "w-full flex items-center justify-between px-4 py-3 text-left transition-colors",
                        isSelected ? "bg-[#FAF5FF]" : "hover:bg-slate-50/70"
                      )}
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        {influencer.avatar ? (
                          <img
                            src={influencer.avatar}
                            alt={name}
                            className="w-10 h-10 rounded-full object-cover shrink-0 border border-slate-100"
                          />
                        ) : (
                          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-400 to-indigo-500 text-white font-semibold flex items-center justify-center text-sm shrink-0">
                            {initial}
                          </div>
                        )}
                        <div className="flex items-center gap-1.5 min-w-0 flex-wrap">
                          <span className="font-semibold text-[14.5px] text-slate-900 truncate">
                            {name}
                          </span>
                          {handle && (
                            <span className="text-[13.5px] text-slate-500 font-normal truncate">
                              (@{handle})
                            </span>
                          )}
                          {influencer.isVerified && (
                            <div className="w-4 h-4 rounded-full bg-[#A655F8] flex items-center justify-center text-white shrink-0">
                              <Check className="w-2.5 h-2.5 stroke-[3]" />
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="flex items-center gap-1.5 text-slate-500 text-[13.5px] shrink-0 ml-3">
                        <Instagram className="w-4 h-4 text-slate-400 stroke-[1.8]" />
                        <span className="capitalize">{platform}</span>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
